import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Drawer,
  Grid,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  FormControl,
  Select,
  InputAdornment,
  Divider
} from '@mui/material';
import { fetchKnrInfo, KnrInfo } from '../../services/knrService';

export interface AddPositionDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert?: (data: PositionFormState) => void;
  lastPositionNr?: string; // Numer ostatniej pozycji dla domyślnej wartości
}

export interface PositionFormState {
  knr: string;
  symbol: string;
  attachment: '' | 'Analiza indywidualna' | 'Analogia';
  type: 'Szczegółowa' | 'Uproszczona';
  quantity: string; // przechowujemy jako string dla łatwiejszej walidacji
  jm: string;
  opis: string;
  insertAfterNr: string; // Numer pozycji, za którą wstawić
}

const defaultState: PositionFormState = {
  knr: '',
  symbol: '',
  attachment: '',
  type: 'Szczegółowa',
  quantity: '',
  jm: '',
  opis: '',
  insertAfterNr: ''
};

const AddPositionDialog: React.FC<AddPositionDialogProps> = ({ open, onClose, onInsert, lastPositionNr }) => {
  const [form, setForm] = useState<PositionFormState>(defaultState);
  const [loading, setLoading] = useState<boolean>(false);
  const [autoFilled, setAutoFilled] = useState<{jm:boolean; opis:boolean}>({jm:false, opis:false});

  // Reset formularza przy otwarciu okna
  useEffect(() => {
    if (open) {
      setForm({
        ...defaultState,
        insertAfterNr: lastPositionNr || ''
      });
    }
  }, [open, lastPositionNr]);

  const fetchInfo = () => {
    const { knr, symbol } = form;
    if (knr.trim() === '' || symbol.trim().length !== 9) return;
    setLoading(true);
    fetchKnrInfo(knr.trim(), symbol.trim())
      .then((data: KnrInfo) => {
        setForm(prev => ({
          ...prev,
          opis: data.opis ?? prev.opis,
          jm: data.jm ?? prev.jm
        }));
        setAutoFilled({
          jm: !!data.jm,
          opis: !!data.opis
        });
      })
      .catch(err => {
        console.error('Błąd podczas pobierania informacji o KNR:', err);
      })
      .finally(() => setLoading(false));
  };

  // Usunięto automatyczne pobieranie – wyszukiwanie uruchamia tylko kliknięcie w lupę

  const handleChange = (field: keyof PositionFormState) => (
    event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleInsert = () => {
    if (onInsert) {
      onInsert(form);
    }
    onClose();
  };

  // Style pól tekstowych jak w AddProjectForm
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '&:has(.MuiSelect-root[aria-expanded="true"]) .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    }
  } as const;

  // Komponent FieldLabel jak w AddProjectForm
  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 450, maxWidth: '100vw', borderRadius: 0, height: '100vh' } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Nagłówek */}
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Dodaj pozycję kosztorysową
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>

        {/* Zawartość formularza */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
          <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid item xs={12}>
              <FieldLabel text="KNR" required />
              <TextField
                fullWidth
                value={form.knr}
                onChange={handleChange('knr')}
                variant="outlined"
                size="small"
                required
                disabled={loading}
                inputProps={{ maxLength: 22 }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Symbol" required />
              <TextField
                fullWidth
                value={form.symbol}
                onChange={handleChange('symbol')}
                variant="outlined"
                size="small"
                required
                disabled={loading}
                inputProps={{ maxLength: 9 }}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={fetchInfo}
                        disabled={loading}
                        aria-label="Szukaj w bazie"
                        size="small"
                        edge="end"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Załącznik" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select
                  name="attachment"
                  value={form.attachment}
                  onChange={handleSelectChange}
                  displayEmpty
                  renderValue={(val) => val || 'Wybierz z listy'}
                  IconComponent={(iconProps: any) => (
                    <Box
                      component="img"
                      alt="dropdown"
                      src="/assets/icons/keyboard_arrow_down.svg"
                      {...iconProps}
                      sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                    />
                  )}
                >
                  <MenuItem value="">&nbsp;</MenuItem>
                  <MenuItem value="Analiza indywidualna">Analiza indywidualna</MenuItem>
                  <MenuItem value="Analogia">Analogia</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Typ pozycji" />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select
                  name="type"
                  value={form.type}
                  onChange={handleSelectChange}
                  IconComponent={(iconProps: any) => (
                    <Box
                      component="img"
                      alt="dropdown"
                      src="/assets/icons/keyboard_arrow_down.svg"
                      {...iconProps}
                      sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                    />
                  )}
                >
                  <MenuItem value="Szczegółowa">Szczegółowa</MenuItem>
                  <MenuItem value="Uproszczona">Uproszczona</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Ilość robót" />
              <TextField
                fullWidth
                value={form.quantity}
                onChange={handleChange('quantity')}
                variant="outlined"
                size="small"
                type="number"
                inputProps={{ step: 0.001 }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Jm" />
              <TextField
                fullWidth
                value={form.jm}
                onChange={handleChange('jm')}
                variant="outlined"
                size="small"
                disabled={loading}
                sx={{
                  ...textFieldSx,
                  '& .MuiInputBase-input': {
                    color: autoFilled.jm ? 'error.main' : '#2D2D32'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Opis" />
              <TextField
                fullWidth
                value={form.opis}
                onChange={handleChange('opis')}
                variant="outlined"
                size="small"
                multiline
                minRows={3}
                disabled={loading}
                sx={{
                  ...textFieldSx,
                  '& .MuiOutlinedInput-root': { height: 'auto', backgroundColor: '#fff', borderRadius: '4px', alignItems: 'flex-start' },
                  '& .MuiInputBase-input': { padding: 0, color: autoFilled.opis ? 'error.main' : '#2D2D32' },
                  '& .MuiOutlinedInput-input': { padding: 0 }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Wstaw za pozycję" />
              <TextField
                fullWidth
                value={form.insertAfterNr}
                onChange={handleChange('insertAfterNr')}
                variant="outlined"
                size="small"
                sx={textFieldSx}
              />
            </Grid>
            {loading && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Ładowanie danych z bazy...
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Stopka z przyciskami */}
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button 
            onClick={onClose} 
            variant="text" 
            disabled={loading}
            sx={{ 
              fontFamily: 'Roboto, sans-serif', 
              fontWeight: 400, 
              fontSize: '14px', 
              color: '#d32f2f', 
              textTransform: 'none' 
            }}
          >
            Usuń
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={onClose} 
              variant="text" 
              disabled={loading}
              sx={{ 
                fontFamily: 'Roboto, sans-serif', 
                fontWeight: 400, 
                fontSize: '14px', 
                color: '#2D2D32', 
                textTransform: 'none' 
              }}
            >
              Anuluj
            </Button>
            <Button 
              onClick={handleInsert} 
              variant="contained" 
              disabled={loading}
              sx={{ 
                backgroundColor: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#030360'
                }
              }}
            >
              Wstaw
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddPositionDialog; 